import React, { useState, useRef, useEffect } from 'react';
import { Menu, ConfigProvider, Input } from 'antd';
import './style.css';
import IconGeo from '../icons/iconMapMenu/IconGeo';
import IconOnline from '../icons/iconOnline/IconOnline';
import IconOffline from '../icons/iconOffline/IconOffline';
import { Device } from '../../types/Device';
import { SearchOutlined } from '@ant-design/icons';

interface SideMenuCamsProps {
    devices: Device[];
    onSelect?: (uid: string) => void;
    onReset?: () => void;
    selectedUID?: string | null;
}

function filterDevicesWithGeo(devices: Device[]) {
    return devices.filter((d) => d.latitude !== 0 && d.longitude !== 0);
}
function filterDevicesWithoutGeo(devices: Device[]) {
    return devices.filter((d) => d.latitude === 0 && d.longitude === 0);
}

function useScrollToSelected(activeUID: string | null, itemRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>) {
    useEffect(() => {
        if (activeUID && itemRefs.current[activeUID]) {
            itemRefs.current[activeUID]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [activeUID]);
}

const DeviceList: React.FC<{
    devices: Device[];
    onSelect?: (uid: string) => void;
    selectedUID?: string | null;
    itemRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    faded?: boolean;
}> = ({ devices, onSelect, selectedUID, itemRefs, faded }) => (
    <Menu
        mode="inline"
        selectedKeys={selectedUID ? [selectedUID] : []}
        style={{ background: '#F1F1F1', borderRight: 0 }}
    >
        {devices.map((device) => (
            <Menu.Item
                className={'body medium-bold'}
                style={{ color: 'var(--primary)' }}
                key={device.UID}
                onClick={onSelect ? () => onSelect(device.UID) : undefined}
            >
                <div
                    ref={el => (itemRefs.current[device.UID] = el)}
                    className={faded ? 'side-menu-cams-device-faded' : 'side-menu-cams-device'}
                >
                    {device.online ? (
                        <IconOnline style={{ marginRight: 8 }} />
                    ) : (
                        <IconOffline style={{ marginRight: 8 }} />
                    )}
                    <span className="side-menu-cams-device-name">{device.name}</span>
                </div>
            </Menu.Item>
        ))}
    </Menu>
);

const SideMenuCams: React.FC<SideMenuCamsProps> = ({ devices, onSelect, onReset, selectedUID }) => {
    const [internalSelectedUID, setInternalSelectedUID] = useState<string | null>(null);
    const [searchText, setSearchText] = useState<string>('');
    const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const handleSelect = (uid: string) => {
        setInternalSelectedUID(uid);
        if (onSelect) onSelect(uid);
    };

    const handleReset = () => {
        setInternalSelectedUID(null);
        if (onReset) onReset();
    };

    const validDevices = filterDevicesWithGeo(devices);
    const noGeoDevices = filterDevicesWithoutGeo(devices);
    const activeUID = selectedUID !== undefined ? selectedUID : internalSelectedUID;

    const normalizedSearch = searchText.trim().toLowerCase();
    const filterByName = (list: Device[]) =>
        normalizedSearch
            ? list.filter((d) => (d.name || '').toLowerCase().includes(normalizedSearch))
            : list;

    const filteredValidDevices = filterByName(validDevices);
    const filteredNoGeoDevices = filterByName(noGeoDevices);

    useScrollToSelected(activeUID, itemRefs);

    return (
        <div className="side-menu-cams-container">
            <div className="side-menu-cams-header">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span className={'headline small side-menu-cams-title'}>
                        Устройства
                    </span>
                    <span className={'headline small side-menu-cams-count'}>
                        ({filteredValidDevices.length})
                    </span>
                    <button
                        className="side-menu-cams-reset-btn"
                        title="Сбросить выделение и центрировать карту"
                        onClick={handleReset}
                    >
                        <IconGeo width={32} height={32} />
                    </button>
                </div>
            </div>
            <div className="side-menu-cams-search">
                <Input
                    placeholder="Поиск"
                    className="side-menu-cams-search-input"
                    suffix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    size="middle"
                />
            </div>
            <ConfigProvider
                theme={{
                    components: {
                        Menu: {
                            itemBg: '#F1F1F1',
                            itemHoverBg: '#E1E5E9',
                            itemSelectedBg: '#D3DADF',
                            subMenuItemBg: '#F1F1F1',
                        },
                    },
                }}
            >
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <DeviceList
                        devices={filteredValidDevices}
                        onSelect={handleSelect}
                        selectedUID={activeUID}
                        itemRefs={itemRefs}
                    />
                    {noGeoDevices.length > 0 && (
                        <>
                            <div className="side-menu-cams-nogeo-divider" />
                            <div className="side-menu-cams-nogeo-header">
                                <span className="side-menu-cams-nogeo-title">Без геолокации</span>
                                <span className="side-menu-cams-nogeo-count">({filteredNoGeoDevices.length})</span>
                            </div>
                            <DeviceList
                                devices={filteredNoGeoDevices}
                                itemRefs={itemRefs}
                                faded
                            />
                        </>
                    )}
                </div>
            </ConfigProvider>
        </div>
    );
};

export default SideMenuCams; 